import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TagField,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IShipco, ISubscription, IVessel } from "interfaces";
import React from "react";
import { getBooleanTagColor } from "constants/tagColors";

export const SubscriptionList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<ISubscription>({
    syncWithLocation: false,
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: {
      mode: "server"
    }
  });

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<ISubscription>({
      mapData: (item) => {
        return {
          "UKHO Licence ID": item?.licence?.ukhoLicenceId || "",
          "Vessel IMO Number": item?.licence?.vessel?.imoNumber || "",
          "Vessel Name": item?.licence?.vessel?.name || "",
          "Subscription Name": item?.subscriptionName || "",
          "Service Type": item?.serviceType || "",
          "Enrollment Date": item?.enrollmentDate || "",
          "Licence Type": item?.licenceType || "",
          "Activation Status": item?.activationStatus || "",
          "Hold Status": item?.holdStatus || "",
          "Order Value Threshold": item?.orderValueThreshold || "",
          "Automated Ordering": item?.automatedOrdering || "",
          "Automated Permit Issuance": item?.automatedPermitIssuance || "",
          "Shipco": item?.licence?.shipco?.registrationName || "",
          "Shipco Group": item?.licence?.shipcoGroup?.registrationName || "",
          "AVCS Service": item?.metadataAvcs ? "yes" : "no",
          "ADP Service": item?.metadataAdp ? "yes" : "no",
          "e-NP Service": item?.metadataEnp ? "yes" : "no",
          "ARCS Service": item?.metadataArcs ? "yes" : "no",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "UKHO Licence ID",
          "Vessel IMO Number",
          "Vessel Name",
          "Subscription Name",
          "Service Type",
          "Enrollment Date",
          "Licence Type",
          "Activation Status",
          "Hold Status",
          "Order Value Threshold",
          "Automated Ordering",
          "Automated Permit Issuance",
          "Shipco",
          "Shipco Group",
          "AVCS Service",
          "ADP Service",
          "e-NP Service",
          "ARCS Service",
        ],
      },
    });

  const columns: ColumnsType<ISubscription> = [
    {
      title: "IMO Number",
      dataIndex: ["licence", "vessel", "imoNumber"],
      key: "licence.vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 250 }}
            mode="multiple"
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["licence", "vessel", "name"],
      key: "licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 300 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco",
      dataIndex: ["licence", "shipco", "registrationName"],
      key: "licence.shipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Activation Status",
      dataIndex: "activationStatus",
      key: "activationStatus",
      width: 120,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "active" : "inactive"}
        />
      ),
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 150 }}
            placeholder="Select Status"
          >
            <Select.Option value={true}>active</Select.Option>
            <Select.Option value={false}>inactive</Select.Option>
          </Select>
        </FilterDropdown>
      ),
    },
    {
      title: "Hold Status",
      dataIndex: "holdStatus",
      key: "holdStatus",
      width: 120,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "not on hold" : "on hold"}
        />
      ),
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 150 }}
            placeholder="Select Status"
          >
            <Select.Option value={true}>not on hold</Select.Option>
            <Select.Option value={false}>on hold</Select.Option>
          </Select>
        </FilterDropdown>
      ),
    },
    {
      title: "Enrollment Date",
      dataIndex: "enrollmentDate",
      key: "enrollmentDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active subscription",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
